import React, { useEffect, useState } from "react";
import { Drawer, Tabs } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { LifecycleRulesContent } from "@/features/lifecycle-rules/lifecycleRulesContent";
import styles from "./LifecycleRulesDrawer.module.scss";

interface LifecycleRulesDrawerProps {
  content: LifecycleRulesContent;
  open: boolean;
  onClose: () => void;
}

function FlatLifecycleBody({ content }: { content: LifecycleRulesContent }) {
  return (
    <>
      <p>
        <strong>Lifecycle Rule:</strong> {content.ruleTitle}
      </p>
      <p>
        <strong>Flow:</strong> {content.flowSummary}
      </p>
      <ul>
        {content.bullets.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {content.limitations && content.limitations.length > 0 ? (
        <>
          <p>
            <strong>Current limitations</strong>
          </p>
          <ul>
            {content.limitations.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

function SongLifecycleDiagram() {
  return (
    <div className={styles.lifecycleDiagram} aria-label="Song lifecycle flow">
      <div className={styles.diagramNode}>Active</div>
      <div className={styles.diagramArrow}>↓</div>
      <div className={styles.diagramNode}>Archived</div>
      <div className={styles.diagramBranch}>
        <div className={styles.diagramBranchCol}>
          <div className={styles.diagramEdgeLabel}>↙ Restore</div>
          <div className={styles.diagramNode}>Active</div>
        </div>
        <div className={styles.diagramBranchCol}>
          <div className={styles.diagramEdgeLabel}>Permanent Delete ↘</div>
          <div className={styles.diagramNode}>Deleted / Tombstone</div>
          <div className={styles.diagramArrow}>↓</div>
          <div className={styles.diagramNodeMuted}>Tombstone Purge (Permanent)</div>
        </div>
      </div>
    </div>
  );
}

function TabbedLifecycleBody({
  content,
  activeKey,
  onChange,
}: {
  content: LifecycleRulesContent;
  activeKey: string;
  onChange: (key: string) => void;
}) {
  const tabs = content.tabs ?? [];
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];

  return (
    <div className={styles.tabbedBody}>
      <p>
        <strong>Lifecycle Rule:</strong> {content.ruleTitle}
      </p>
      <p>
        <strong>Flow:</strong> {content.flowSummary}
      </p>
      <Tabs
        type="card"
        activeKey={active?.key}
        onChange={onChange}
        size="small"
        tabBarGutter={0}
        items={tabs.map((tab) => ({
          key: tab.key,
          label: tab.label,
          children: (
            <div>
              {tab.showLifecycleDiagram ? <SongLifecycleDiagram /> : null}
              {tab.intro ? <p style={{ marginBottom: 12 }}>{tab.intro}</p> : null}
              {tab.sections?.map((section) => (
                <div key={section.title} className={styles.sectionBlock}>
                  <p className={styles.sectionTitle}>{section.title}</p>
                  <p className={styles.sectionBody}>{section.body}</p>
                </div>
              ))}
              <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                {tab.bullets.map((line) => (
                  <li key={line} style={{ marginBottom: 8 }}>
                    {line}
                  </li>
                ))}
              </ul>
              {tab.limitations && tab.limitations.length > 0 ? (
                <>
                  <p style={{ marginTop: 16, marginBottom: 8 }}>
                    <strong>Current limitations</strong>
                  </p>
                  <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                    {tab.limitations.map((line) => (
                      <li key={line} style={{ marginBottom: 8 }}>
                        {line}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          ),
        }))}
      />
    </div>
  );
}

const LifecycleRulesDrawer: React.FC<LifecycleRulesDrawerProps> = ({
  content,
  open,
  onClose,
}) => {
  const hasTabs = Boolean(content.tabs && content.tabs.length > 0);
  const defaultTabKey = content.tabs?.[0]?.key ?? "lifecycle";
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey);

  // Default to Overview (first tab) each time the drawer is newly opened.
  // While open, preserve the operator's selected tab.
  useEffect(() => {
    if (open && hasTabs && content.tabs?.[0]?.key) {
      setActiveTabKey(content.tabs[0].key);
    }
  }, [open, hasTabs, content.tabs]);

  return (
    <Drawer
      title="Lifecycle Rules"
      placement="right"
      width={hasTabs ? 480 : 420}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{
        body: {
          paddingTop: 12,
        },
      }}
      // Keep usable on narrow viewports without inventing a second pattern.
      rootStyle={{ maxWidth: "100vw" }}
    >
      {hasTabs ? (
        <TabbedLifecycleBody
          content={content}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
        />
      ) : (
        <FlatLifecycleBody content={content} />
      )}
    </Drawer>
  );
};

interface LifecycleRulesInfoButtonProps {
  onClick: () => void;
  ariaLabel?: string;
  title?: string;
}

export function LifecycleRulesInfoButton({
  onClick,
  ariaLabel = "Lifecycle rules help",
  title = "Lifecycle rules",
}: LifecycleRulesInfoButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      style={{
        background: "none",
        border: "none",
        padding: "0 8px",
        color: "#666",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <InfoCircleOutlined style={{ fontSize: 18 }} />
    </button>
  );
}

export default LifecycleRulesDrawer;
